import { useState } from "react";
import axios from "axios";
import { ArrowLeft, ShoppingCart } from "lucide-react";
import { API, LOGO_IMAGE } from "./constants";

const CREDIT_PACKS = [
  { id: "price_1TRB5X3X4a4k0cx3kcpUn2dG", credits: 50, price: "£2.49", name: "Starter Pack", stars: "⭐", description: "Perfect for a few chats" },
  { id: "price_1TRB5b3X4a4k0cx3TwsJaG0M", credits: 100, price: "£3.99", name: "Family Pack", stars: "⭐⭐", description: "Great value for regular use" },
  { id: "price_1TRB5a3X4a4k0cx3mhoT1nnD", credits: 300, price: "£9.49", name: "Supporter Pack", stars: "⭐⭐⭐", description: "Best for daily chatters" },
  { id: "price_1TRB5X3X4a4k0cx3ovWZeiTt", credits: 400, price: "£11.99", name: "Best Value Pack", stars: "⭐⭐⭐⭐", description: "Maximum chat time" },
];

const BuyCredits = ({ onBack, onGoOrganisations }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const handleBuy = async (priceId) => {
    setLoading(priceId);
    setError('');
    try {
      const res = await axios.post(`${API}/checkout`, {
        price_id: priceId,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-[#F3F0E6] transition-colors" data-testid="buy-back-button">
            <ArrowLeft className="w-6 h-6 text-[#4A4A4A]" />
          </button>
          <a href="https://www.poodlesandfriends.uk/" target="_blank" rel="noopener noreferrer">
            <img src={LOGO_IMAGE} alt="Poodles and Friends" className="h-16 w-auto object-contain" />
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8 sm:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#4A4A4A] mb-3">Get Chat Credits</h1>
          <p className="text-[#6B6B6B] text-base sm:text-lg">
            Give your child more time to chat with their poodle friend
          </p>
          <p className="text-sm text-[#9A9A9A] mt-2">
            Each credit = one personalised AI message. Picture cards are always free.
          </p>
        </div>

        {error && (
          <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-2xl p-4 mb-6 text-center" data-testid="buy-error">
            <p className="text-sm text-[#A55B64]">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CREDIT_PACKS.map((pack) => (
            <div
              key={pack.id}
              className="bg-white rounded-2xl border-2 border-[#E8E5DC] p-5 hover:border-[#C9B896] hover:shadow-lg transition-all"
            >
              <p className="text-center text-sm font-semibold text-[#B89823] mb-1">{pack.stars}</p>
              <p className="text-center text-xs font-bold text-[#6B6B6B] uppercase tracking-wide mb-3">{pack.name}</p>
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-[#4A4A4A]">{pack.credits}</p>
                <p className="text-sm text-[#6B6B6B]">chat credits</p>
              </div>
              <p className="text-center text-2xl font-bold text-[#B89823] mb-2">{pack.price}</p>
              <p className="text-center text-xs text-[#9A9A9A] mb-4">{pack.description}</p>
              <button
                onClick={() => handleBuy(pack.id)}
                disabled={loading !== null}
                data-testid={`buy-pack-${pack.credits}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {loading === pack.id ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    Buy Now
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center space-y-3">
          <div className="bg-[#F3E8FF] rounded-2xl p-4 inline-block">
            <p className="text-sm text-[#4A4A4A]">
              <strong>Remember:</strong> Picture cards, emotions, and quick messages are always <strong className="text-[#5A8A5A]">FREE</strong> — credits are only for typed/spoken messages.
            </p>
          </div>
          <p className="text-xs text-[#9A9A9A]">
            After purchase, your code will be emailed to you instantly.<br />
            Enter the code in Parents Corner or on the cooldown screen.
          </p>
          <div className="pt-2">
            <button
              onClick={() => onGoOrganisations && onGoOrganisations()}
              data-testid="buy-credits-org-signin-link"
              className="text-xs text-[#6B6B6B] hover:text-[#4A4A4A] underline underline-offset-2 transition-colors"
            >
              School, Organisation or Hotel Sign In
            </button>
          </div>
        </div>
      </main>

      <footer className="py-4 px-4 text-center">
        <p className="text-xs text-[#9A9A9A]">
          Secure payment powered by Stripe. All transactions are encrypted.
        </p>
      </footer>
    </div>
  );
};

export default BuyCredits;
